import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { useOrbitContext } from '../OrbitContext';
import { TOrbit } from '../i18n/TModules';
import {
  ORBIT_GLOBAL_STARTERS,
  OrbitPromptStarter,
  getContextualStarters,
  isStarterAllowed
} from '../promptStarters';
import { OrbitSectionLabel, orbitTokens, orbitType } from '../styles';
import useOrbitStarters from '../useOrbitStarters';

// TODO(AF-11): the idle frame draws this label Bold 11 / 0.44; the panel-wide settled decision is
// 10.5 / 0.525, which OrbitSectionLabel carries.
const Label = styled(OrbitSectionLabel)`
  padding-top: 16px;
  margin-bottom: 10px;
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-top: 8px;
  }
`;

// sq-item 4952:5408: filled, single-line and arrow-trailed, with no icon tile and no subtitle —
// deliberately a different row from the home state's action row. 13.8 + 16 + 13.8 is its drawn 43.6.
const Row = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  /* 4952:5408 is a 43.6 box with its strokes inside it, as the chip is: 13.8 + 16 + 13.8. */
  height: 43.6px;
  width: 100%;
  margin: 0;
  padding: 13.8px 15.8px;
  background: ${orbitTokens.surfaceAlt};
  border: ${orbitTokens.borderWidth} solid ${orbitTokens.border};
  border-radius: 10px;
  color: ${orbitTokens.text};
  cursor: pointer;
  font-family: inherit;
  ${orbitType.containerBody}
  font-weight: 400;
  text-align: left;

  &:hover:not(:disabled),
  &:focus-visible {
    border-color: ${orbitTokens.action};
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const RowLabel = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Arrow = styled.span`
  flex: 0 0 auto;
  margin-left: 12px;
`;

// DELIBERATE ADDITION: no idle frame draws it. The header's history control was removed under the
// v3 redesign and re-homed to the home state, so without this row the saved investigations are
// unreachable from every screen that resolves a context. Its own copy already carries the arrow.
const ViewAll = styled(Row)`
  justify-content: center;
  margin-top: 12px;
`;

export interface IOrbitIdleStateProps {
  /** Opens the conversation list, as the home state's own View all history does. */
  onViewHistory: () => void;
}

/**
 * Idle panel `4952:5397`: the suggested queries under the context block, shown before the first
 * question on a screen Orbit recognises. The context header and chips are not drawn here — they are
 * the shell's sticky `OrbitContextBlock`, which the same condition puts on screen.
 */
const OrbitIdleState: React.FC<IOrbitIdleStateProps> = ({ onViewHistory }) => {
  const { t } = useMultiLanguageTranslation();
  const { ask, isAsking } = useOrbitContext();
  const { screen, cards, grants } = useOrbitStarters();

  const starters = useMemo(
    () =>
      [...ORBIT_GLOBAL_STARTERS, ...getContextualStarters(screen, cards)].filter(starter =>
        isStarterAllowed(starter, grants)
      ),
    [screen, cards, grants]
  );

  const handleStarterClick = useCallback(
    (starter: OrbitPromptStarter) => {
      ask(t(starter.labelKey));
    },
    [ask, t]
  );

  return (
    <div data-testid="orbit-idle-state">
      <Label id="orbit-idle-queries-heading">{t(`${TOrbit}.idle.suggested`)}</Label>
      <List aria-labelledby="orbit-idle-queries-heading">
        {starters.map(starter => (
          <li key={starter.id}>
            <Row
              type="button"
              disabled={isAsking}
              data-testid={`orbit-idle-query-${starter.id}`}
              onClick={() => handleStarterClick(starter)}
            >
              <RowLabel>{t(starter.labelKey)}</RowLabel>
              <Arrow aria-hidden="true">→</Arrow>
            </Row>
          </li>
        ))}
      </List>
      <ViewAll type="button" data-testid="orbit-view-history" onClick={onViewHistory}>
        {t(`${TOrbit}.home.viewAll`)}
      </ViewAll>
    </div>
  );
};

export default OrbitIdleState;
